<script>
  import {getContext, onMount} from 'svelte';
  import {mc} from '@/core';
  import {premiumApi} from '@/api/premium';
  import {tooltip} from '@/directives';
  import {sd} from '@/da';
  import Button from '@/widgets/Button.svelte';
  import Modal from '@/widgets/Modal.svelte';
  import CoinSelect from '@/modules/components/CoinSelect.svelte';
  import {purchasePlans$, supportCurrencies$} from '@/modules/premium/stream';
  import {NOTIFICATION_WARING} from '@/modules/premium/const';
  import {coin$, amount$, totalAmount$} from './BuyPremiumPopup.stream';
  import errors from '@/modules/error/error.lang';
  import premiumLang from '@/modules/premium/PremiumPage.lang';
  import lang from './BuyPremiumPopup.lang';

  const LG = getContext('LG');
  const BUY_TEXT = LG(lang.BuyText);

  let visible = false;
  let type = 'purchasePlan30V1';
  let amount = '9.9';
  let isPosting = false;
  let coin;

  $: coin = $coin$;

  onMount(() =>
    mc.subscribe('buy-premium', 'dialog-show', ({record}) => {
      coin$.next('BTC');
      type = record.membershipType;
      amount = record.price;
      amount$.next(record.price);
      visible = true;
    })
  );

  function handleCheck(item) {
    sd.track('choose_premium_days', {membership_days: item.days});
    type = item.membershipType;
    amount = item.price;
    amount$.next(item.price);
  }

  async function handleClickBuy() {
    isPosting = true;
    const query = {
      membershipType: type,
      currencyName: coin,
      autoRenew: '0',
    };
    const {data, error} = await premiumApi.buyMembershipPurchase(query);
    visible = false;
    if (data) {
      const record = {type: 'success', membershipType: type, price: amount, data};
      mc.send('buy-success', 'dialog-show', {record});
      mc.send('reload-base-membership', 'action');
      mc.send('reload-share-time', 'action');
    }
    if (error) {
      const {code, message} = error;
      const displayMessage = LG(errors[code]) || message;
      const record = {type: 'error', membershipType: type, price: amount, code, message: displayMessage};
      mc.send('buy-success', 'dialog-show', {record});
    }
    isPosting = false;
  }

  function coinSelectConverter(options) {
    return options.filter((o) => $supportCurrencies$.indexOf(o.value) > -1);
  }
</script>

<style>
  .modal_left {
    padding: 36px 30px;
    width: 210px;
    background: #003398;
  }
  .left_title_2 {
    opacity: 0.4;
  }
  .lh16 {
    line-height: 16px;
  }
  .left_title {
    line-height: 17px;
    font-size: 14px;
  }
  .card {
    border-radius: 4px;
    padding: 4px 24px 24px;
    background: #f9fafd;
  }
  .checked {
    color: #003398;
  }
  .img {
    width: 12px;
  }
  .close {
    margin-top: -10px;
    margin-right: -8px;
  }
  .main {
    margin: -24px;
  }
  @media screen and (min-width: 640px) {
    .modal_right {
      padding: 36px 32px;
      width: 306px;
      background: #fff;
    }
  }
  @media screen and (max-width: 640px) {
    .modal_left {
      display: none;
    }
    .modal_right {
      padding: 24px;
      width: 100%;
    }
  }
</style>

{#if visible}
  <Modal style="overflow: visible;" {visible} size="big">
    <div class="main df fdr">
      <div class="modal_left">
        <div class="left_title fw2 cf">{LG(lang.After)}</div>
        <div class="left_title_2 mt16 cf">{LG(lang.Spot)}</div>
        <div class="f14 lh16 mt8 cf">{LG(lang.Spot_1)} ({LG(premiumLang.DailyLimit)} {LG(premiumLang.MonthlyLimit)})</div>
        <div class="f14 lh16 mt8 cf">{LG(lang.Spot_2)}</div>
        <div class="f14 lh16 mt8 cf">{LG(lang.Spot_3)}</div>
        <div class="left_title_2 mt24 cf">{LG(lang.Others)}</div>
        <div class="f14 lh16 mt12 cf">{LG(lang.Others_1)}</div>
        <div class="f14 lh16 mt12 cf">{LG(lang.Others_2)}</div>
        <div class="f14 lh16 mt12 cf">{LG(lang.Others_3)}</div>
        <div class="left_title_2 mt36 cf f12">{LG(lang.More)}</div>
      </div>
      <div class="modal_right T1">
        <div class="df jcfe close">
          <i class="iconfont lh16 f16 T4 cp" on:click={() => (visible = false)}>&#xe614;</i>
        </div>
        <div class="right_title f16 T1">{LG(lang.BuyPremium)}</div>
        <div class="card mt12">
          {#each $purchasePlans$ as item (item.price)}
            <div class="df jcsb mt20">
              <div class="df aic f14 lh16" class:T2={type === item.membershipType} class:T3={type !== item.membershipType}>
                <i class="iconfont T4 cp mr8" on:click={handleCheck(item)} class:checked={type === item.membershipType}>
                  {@html type === item.membershipType ? '&#xe621;' : '&#xe622;'}
                </i>
                {item.days}
                {LG(lang.Days)}
              </div>
              <div class="fw2 T2 f14">{item.price} USD</div>
            </div>
          {/each}
        </div>
        <div>
          <div class="mt20 T2 f14 lh22">{LG(lang.PaymentCoin)}</div>
          <div>
            <CoinSelect {coin} converter={coinSelectConverter} on:change={(e) => coin$.next(e.detail.value)} />
          </div>
        </div>
        <div class="df jcsb mt24">
          <div class="T2 f14 lh22">{LG(lang.Total)}</div>
          <div class="T2 f14 lh22 df">
            {#if coin === 'USDT'}= {amount}{:else}≈ {$totalAmount$}{/if}
            {coin}
            {#if coin !== 'USDT'}
              <div class="ml4 cp" use:tooltip={{content: BUY_TEXT, placement: 'bottom', background: '#fff', color: 'var(--T1)'}}>
                <img class="img" src={NOTIFICATION_WARING} alt="Notification" />
              </div>
            {/if}
          </div>
        </div>
        <div class="mt12">
          <Button type="primary" minWidth="100%" loading={isPosting} on:click={handleClickBuy}>{LG(lang.PAY)}</Button>
        </div>
      </div>
    </div>
  </Modal>
{/if}
